import React, { useCallback, useMemo } from 'react';
import { useMaxWave } from 'contexts/gameState';
import { useResults } from 'contexts/results';
import formatCoins from 'utils/formatCoins';
import formatTime from 'utils/formatTime';
import { useCheckboxState, useUpdatedState } from 'utils/hooks';

export const DEFAULT_TIERS = [7, 8, 9, 10];

export const TIER_MULTIPLIERS = {
  1: 1,
  2: 1.8,
  3: 2.6,
  4: 3.4,
  5: 4.2,
  6: 5,
  7: 5.8,
  8: 6.6,
  9: 7.5,
  10: 8.6,
  11: 9.9,
  12: 11.5,
};
export const TIERS = Object.keys(TIER_MULTIPLIERS).sort(
  (a, b) => parseInt(a, 10) - parseInt(b, 10),
);

const ResultsTable: React.FC<{}> = () => {
  const results = useResults();
  const maxWave = useMaxWave();

  const [showTiers, setShowTiers] = useUpdatedState(DEFAULT_TIERS, 'showTiers');
  const [showTotalCoins, setShowTotalCoins] = useCheckboxState(
    true,
    'showTotalCoins',
  );
  const [showCPH, setShowCPH] = useCheckboxState(true, 'showCPH');
  const [showCPW, setShowCPW] = useCheckboxState(true, 'showCPW');
  const updateShowTiers = useCallback(
    ({ target: { value, checked } }) => {
      setShowTiers(tiers => {
        let newTiers = [...tiers];
        if (!checked) {
          newTiers = newTiers.filter(i => i !== parseInt(value, 10));
        } else if (!newTiers.includes(value)) {
          newTiers.push(parseInt(value, 10));
        }
        newTiers.sort((a, b) => a - b);
        return newTiers;
      });
    },
    [setShowTiers],
  );
  const waveThresholds: number[] = useMemo(() => {
    const thresholds: number[] = [];
    for (let i = 100; i < maxWave; i += 100) thresholds.push(i);
    thresholds.push(maxWave);
    return thresholds;
  }, [maxWave]);

  const colspan = useMemo(
    () => [showTotalCoins, showCPH, showCPW].filter(i => !!i).length,
    [showCPH, showCPW, showTotalCoins],
  );
  return (
    <div id='table'>
      <div>Averages per tier, assuming you always live that long.</div>
      <div className='show-columns'>
        <label htmlFor='show-total-coins'>
          Show total coins
          <input
            id='show-total-coins'
            type='checkbox'
            checked={showTotalCoins}
            onChange={setShowTotalCoins}
          />
        </label>
        <label htmlFor='show-cph'>
          Show CPH
          <input
            id='show-cph'
            type='checkbox'
            checked={showCPH}
            onChange={setShowCPH}
          />
        </label>
        <label htmlFor='show-cpw'>
          Show CPW
          <input
            id='show-cpw'
            type='checkbox'
            checked={showCPW}
            onChange={setShowCPW}
          />
        </label>
      </div>
      <div className='tiers'>
        Show tiers:{' '}
        {TIERS.map(tier => (
          <label htmlFor={`show-tier-${tier}`} key={tier}>
            {tier}
            <input
              id={`show-tier-${tier}`}
              type='checkbox'
              value={tier}
              checked={showTiers.includes(parseInt(tier, 10))}
              onChange={updateShowTiers}
            />
          </label>
        ))}
      </div>
      <table cellSpacing={0}>
        <thead>
          <tr>
            <td colSpan={2} />
            {colspan > 0 &&
              showTiers.map(tier => (
                <React.Fragment key={tier}>
                  <th colSpan={colspan}>Tier {tier}</th>
                </React.Fragment>
              ))}
          </tr>
          <tr>
            <th>Wave</th>
            <th className='time'>Time</th>
            {colspan > 0 &&
              showTiers.map(tier => (
                <React.Fragment key={tier}>
                  {showTotalCoins && <th className='coins'>Coins</th>}
                  {showCPH && <th className='cph'>CPH</th>}
                  {showCPW && <th className='cpw'>CPW</th>}
                </React.Fragment>
              ))}
          </tr>
        </thead>
        <tbody>
          {waveThresholds.map(wave => (
            <tr key={wave}>
              <td className='wave'>{wave}</td>
              <td className='time'>
                {formatTime(results.stats.waves[wave]?.time || 0)}
              </td>
              {colspan > 0 &&
                showTiers.map(tier => (
                  <React.Fragment key={tier}>
                    {showTotalCoins && (
                      <td className='coins'>
                        {formatCoins(
                          (results.stats.waves[wave]?.coins || 0) *
                            TIER_MULTIPLIERS[tier],
                        )}
                      </td>
                    )}
                    {showCPH && (
                      <td className='cph'>
                        {formatCoins(
                          (results.stats.waves[wave]?.cph || 0) *
                            TIER_MULTIPLIERS[tier],
                        )}
                      </td>
                    )}
                    {showCPW && (
                      <td className='cpw'>
                        {formatCoins(
                          ((results.stats.waves[wave]?.coins || 0) *
                            TIER_MULTIPLIERS[tier]) /
                            wave,
                        )}
                      </td>
                    )}
                  </React.Fragment>
                ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ResultsTable;
